"use client"

import * as React from "react"
import { Check, ChevronDown, Plus, X } from "lucide-react"

import type { Skill } from "@/lib/api"

/**
 * Compact skill selector: a dropdown that adds inline chips.
 *
 * Cards took a whole screen for three options. Chips keep the selection
 * visible and the picker out of the way, and scale to a catalogue of twenty
 * skills without the form growing.
 */
export function SkillPicker({
  skills,
  selected,
  onChange,
}: {
  skills: Skill[]
  selected: string[]
  onChange: (ids: string[]) => void
}) {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false)
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onEsc)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onEsc)
    }
  }, [open])

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])

  const byId = React.useMemo(() => new Map(skills.map((w) => [w.id, w])), [skills])

  return (
    <div ref={ref} className="relative">
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent p-1.5">
        {selected.map((id) => {
          const w = byId.get(id)
          if (!w) return null
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 py-1 pl-2.5 pr-1.5 text-xs font-medium text-foreground"
            >
              {w.title}
              {w.effectful && <span className="text-[9px] text-muted-foreground">effectful</span>}
              <button
                type="button"
                onClick={() => toggle(id)}
                className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={`Remove ${w.title}`}
              >
                <X className="size-3" />
              </button>
            </span>
          )
        })}

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
        >
          {selected.length === 0 ? (
            <>
              <Plus className="size-3" />
              Add skills
            </>
          ) : (
            <ChevronDown className="size-3" />
          )}
        </button>
      </div>

      {open && (
        <div className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-md border bg-popover shadow-lg">
          {skills.map((w) => {
            const on = selected.includes(w.id)
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => toggle(w.id)}
                className="flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/60"
              >
                <span
                  className={
                    "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border " +
                    (on ? "border-primary bg-primary" : "border-muted-foreground/40")
                  }
                >
                  {on && <Check className="size-2.5 text-primary-foreground" />}
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="text-sm">{w.title}</span>
                    {w.effectful && (
                      <span className="rounded bg-muted px-1 text-[9px] text-muted-foreground">
                        effectful
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {w.description}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
